import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { IUploadLog } from '~/client/graph'
import DesktopFileInput from '~/client/src/desktop/components/FileInput/DesktopFileInput'
import { getAttachmentFileType } from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import FileType from '~/client/src/shared/enums/FileType'

import AttachmentFileIcon from '../AttachmentFileIcon'

import './AttachmentIconWithPreview.scss'

interface IProps {
  uploadLog: IUploadLog
  fileName: string
  fileType: string
  className?: string
}

const PREVIEWABLE_FILE_TYPES = [
  FileType.Image,
  FileType.Video,
  FileType.Pdf,
  FileType.Word,
  FileType.Excel,
  FileType.PowerPoint,
]

const useLazyLoading = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasLoaded) {
          setIsInView(true)
          setHasLoaded(true)
          observer.unobserve(element)
        }
      },
      { threshold },
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [threshold, hasLoaded])

  return { ref, isInView, hasLoaded }
}

const AttachmentIconWithPreview: React.FC<IProps> = ({
  uploadLog,
  fileType,
  className = 'mr8 no-grow',
}) => {
  const attachmentFileType = getAttachmentFileType(fileType, '/', false)
  const { ref, isInView } = useLazyLoading(0.1)

  const isPreviewableFile = (fileType: FileType | string): boolean => {
    return PREVIEWABLE_FILE_TYPES.includes(fileType as FileType)
  }

  const getDocumentColorClass = (fileType: FileType): string => {
    switch (fileType) {
      case FileType.Word:
        return 'word-color'
      case FileType.Excel:
        return 'excel-color'
      case FileType.PowerPoint:
      case FileType.Pdf:
        return 'powerpoint-color'
      default:
        return 'default-color'
    }
  }

  const getDocumentAbbreviation = (fileType: FileType): string => {
    switch (fileType) {
      case FileType.Word:
        return 'W'
      case FileType.Excel:
        return 'E'
      case FileType.PowerPoint:
        return 'P'
      case FileType.Pdf:
        return 'PDF'
      default:
        return '?'
    }
  }

  const renderBigFileIcon = (fileType: string): JSX.Element => {
    return (
      <div className="attachment-big-icon">
        <AttachmentFileIcon fileName={fileType} className="" separator="/" />
      </div>
    )
  }

  const renderDocumentThumbnail = (fileType: FileType): JSX.Element => {
    return (
      <div className="attachment-document-thumbnail">
        <div className="attachment-document-preview">
          <div className="document-line line-1" />
          <div className="document-line line-2" />
          <div className="document-line line-3" />
          <div
            className={`document-type-indicator ${getDocumentColorClass(
              fileType,
            )}`}
          >
            {getDocumentAbbreviation(fileType)}
          </div>
        </div>
      </div>
    )
  }

  const renderTypeIcon = (fileType: string): JSX.Element => {
    return (
      <div className="attachment-type-badge">
        <AttachmentFileIcon fileName={fileType} className="" separator="/" />
      </div>
    )
  }

  const renderPreviewThumbnail = (
    fileType: FileType | string,
    uploadLog: IUploadLog,
  ): JSX.Element => {
    const { fileUrl } = uploadLog

    switch (fileType) {
      case FileType.Image:
        if (!isInView) {
          return (
            <div className="attachment-thumbnail attachment-placeholder">
              <AttachmentFileIcon
                fileName={fileType}
                className="placeholder-icon"
                separator="/"
              />
            </div>
          )
        }
        return (
          <DesktopFileInput
            id={uploadLog.id}
            name=""
            value={uploadLog.fileUrl}
            isReadonly={true}
            textClassName="hint"
            shouldHideIconAndOutline={true}
            shouldHideName={true}
            shouldUseFullSize={true}
            className="unclickable-element attachment-thumbnail tw:overflow-hidden"
            projectId={uploadLog.projectId}
          />
        )
      case FileType.Video:
        if (!isInView) {
          return (
            <div className="attachment-thumbnail attachment-placeholder">
              <AttachmentFileIcon
                fileName={fileType}
                className="placeholder-icon"
                separator="/"
              />
            </div>
          )
        }
        return (
          <video
            src={fileUrl}
            className="attachment-thumbnail"
            muted
            preload="none"
          />
        )
      case FileType.Pdf:
      case FileType.Word:
      case FileType.Excel:
      case FileType.PowerPoint:
        return renderDocumentThumbnail(fileType)
      default:
        return renderBigFileIcon(fileType)
    }
  }

  if (!isPreviewableFile(attachmentFileType) || !uploadLog.fileUrl) {
    return (
      <div
        ref={ref}
        className={`${className} attachment-icon-with-preview no-grow relative`}
      >
        {renderPreviewThumbnail(attachmentFileType, uploadLog)}
        {renderTypeIcon(fileType)}
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={`${className} attachment-icon-with-preview no-grow relative`}
    >
      {renderPreviewThumbnail(attachmentFileType, uploadLog)}
      {renderTypeIcon(fileType)}
    </div>
  )
}

export default AttachmentIconWithPreview
